/**
 * The three-way merge an editor runs when its save is refused for a stale `plan_version` (409):
 * someone else saved the same record meanwhile, and the user's draft is kept rather than thrown
 * away. Each field is merged on its own, against the values the edit started from.
 *
 * The draft is a flat record of field key to value — the schedule card keys a month's target and
 * price and the tolerance, the producer-prices dialog each producer line and the trade owner — so
 * one rule serves both.
 */

/** A field both sides changed, to different values: the user picks one before saving again. */
export interface DraftConflict<V> {
  mine: V;
  theirs: V;
}

export interface DraftMergeInput<V> {
  /** The saved values the edit started from. */
  base: Readonly<Record<string, V>>;
  /** The saved values now. A key missing here is a field that no longer exists. */
  theirs: Readonly<Record<string, V>>;
  /** The draft. */
  mine: Readonly<Record<string, V>>;
  /** Fields that can no longer change (a month or delivery that shipped meanwhile): they take the saved value. */
  locked?: ReadonlySet<string>;
  equals?: (left: V, right: V) => boolean;
}

/**
 * @property values - The new draft, one entry per field of `theirs`. A conflicting field holds the
 *   user's value until the conflict is resolved.
 * @property takenOver - Fields the user left alone that someone else changed, or that did not exist
 *   when the edit started; they now hold the saved value.
 * @property dropped - Fields the user changed that are now locked; the change is discarded.
 * @property removed - Fields of the edit that no longer exist, so they leave the draft.
 */
export interface DraftMergeResult<V> {
  values: Record<string, V>;
  conflicts: Record<string, DraftConflict<V>>;
  takenOver: string[];
  dropped: string[];
  removed: string[];
}

/**
 * Merges a draft onto the latest saved values, field by field against `base`: only mine changed
 * keeps mine, only theirs changed takes theirs, both changed to the same value takes it, and both
 * changed differently is a conflict. After the merge, `theirs` is the new base the next save is
 * composed against.
 */
export function mergeDraft<V>({
  base,
  theirs,
  mine,
  locked = new Set<string>(),
  equals = Object.is,
}: DraftMergeInput<V>): DraftMergeResult<V> {
  const result: DraftMergeResult<V> = {
    values: {},
    conflicts: {},
    takenOver: [],
    dropped: [],
    removed: Object.keys(base).filter((key) => !(key in theirs)),
  };

  for (const [key, saved] of Object.entries(theirs)) {
    result.values[key] = saved;
    if (!(key in base) || !(key in mine)) {
      result.takenOver.push(key);
      continue;
    }

    const mineChanged = !equals(mine[key], base[key]);
    const theirsChanged = !equals(saved, base[key]);
    if (locked.has(key)) {
      if (mineChanged && !equals(mine[key], saved)) result.dropped.push(key);
      else if (theirsChanged) result.takenOver.push(key);
    } else if (mineChanged && !theirsChanged) {
      result.values[key] = mine[key];
    } else if (theirsChanged && !mineChanged) {
      result.takenOver.push(key);
    } else if (mineChanged && !equals(mine[key], saved)) {
      result.values[key] = mine[key];
      result.conflicts[key] = { mine: mine[key], theirs: saved };
    }
  }
  return result;
}

/** Whether any field of the draft differs from the saved values it is composed against. */
export const draftDiffers = <V>(
  draft: Readonly<Record<string, V>>,
  base: Readonly<Record<string, V>>,
  equals: (left: V, right: V) => boolean = Object.is
): boolean =>
  Object.entries(draft).some(
    ([key, value]) => !(key in base) || !equals(value, base[key])
  );
